import { createContext, useContext } from 'react';
import { FormControl, FormController } from '@insurancemenu/imenu-components-lib/dist/utils/forms';
import { makeAutoObservable, runInAction } from 'mobx';
import { createGroup } from 'api/operations/groupService';
import { GeneralRouteV2Id } from 'routes/routesConfigV2';
import { SearchInfo } from '@insurancemenu/imenu-components-lib/dist/types/SearchBlock';
import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types/Toast';
import { toastManager } from '@insurancemenu/imenu-components-lib/dist/components/Toast/toastManager';
import { redirectToRoute } from 'routes/redirectToRoute';
import { SearchGroupDelegate } from './searchGroupDelegate';

export default class InitiateGroupPageStore {
  private _isActionLoading = false;

  public readonly searchGroupDelegate = new SearchGroupDelegate();

  public get isActionLoading(): boolean {
    return this._isActionLoading;
  }

  public groupForm = FormController.create(
    {
      name: new FormControl<string>('', { validations: ['printableCharacters'] }),
      firstName: new FormControl<string>('', { validations: ['validName'], optional: true }),
      lastName: new FormControl<string>('', { validations: ['validName'], optional: true }),
      primaryPhone: new FormControl<string>('', { validations: ['isValidUSAPhoneNumber'], optional: true }),
      primaryEmail: new FormControl<string>('', { validations: ['isEmailValid'], optional: true }),
      title: new FormControl<string>('', { optional: true }),
      city: new FormControl<string>('', { validations: ['printableCharacters'], optional: true }),
      street: new FormControl<string>('', {
        validations: ['printableCharacters'],
        optional: true,
        requiredWithFields: ['street2'],
      }),
      street2: new FormControl<string>('', { validations: ['printableCharacters'], optional: true }),
      notes: new FormControl<string>('', { optional: true }),
    },
    {
      capabilities: [{ type: 'zip', params: { zip: { optional: true } } }],
    },
  );

  public applyGroupSearchQuery(value: string): void {
    this.groupForm.fields.name.value = value;
    void this.searchGroupDelegate.searchGroups(value);
  }

  public selectGroupFromSearch(info: SearchInfo): void {
    this.groupForm.fields.name.value = info.name;
    this.searchGroupDelegate.resetFilteredGroups();
    redirectToRoute(GeneralRouteV2Id.INITIATE_PROPOSAL, { groupId: info.id });
  }

  public async createGroupAndNavigateToInitiateProposal(): Promise<void> {
    this._isActionLoading = true;
    try {
      const newGroup = await createGroup({
        name: this.groupForm.fields.name.value,
        contactInfo: {
          firstName: this.groupForm.fields.firstName.value,
          lastName: this.groupForm.fields.lastName.value,
          primaryPhone: this.groupForm.fields.primaryPhone.value,
          primaryEmail: this.groupForm.fields.primaryEmail.value,
          title: this.groupForm.fields.title.value,
          primaryAddress: {
            city: this.groupForm.fields.city.value,
            street: this.groupForm.fields.street.value,
            street2: this.groupForm.fields.street2.value,
            zip: this.groupForm.fields.zip.value,
            state: this.groupForm.fields.state.value,
            county: this.groupForm.fields.county.value,
          },
        },
        effectiveDate: '',
        id: '',
        source: 'AGENT_V2',
        // TODO: Keep this structure for now; update to null once backend supports it
        // agentOfRecord: null,
        agentOfRecord: {
          id: '',
          name: '',
        },
        accountRepresentative: null,

        notes: this.groupForm.fields.notes.value,
      });

      runInAction(() => {
        this._isActionLoading = false;
        redirectToRoute(GeneralRouteV2Id.INITIATE_PROPOSAL, { groupId: newGroup.id });
      });
    } catch {
      runInAction(() => {
        this._isActionLoading = false;
      });
      toastManager.addToast('Something went wrong during group creation', ToastColorTypes.WARNING);
    }
  }

  public get canCompleteStep(): boolean {
    return this.groupForm.basicFieldsValidationsPassed && !this.isActionLoading && !this.groupForm.isLoading;
  }

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }
}

const InitiateGroupPageStoreContext = createContext(new InitiateGroupPageStore());

export const useInitiateGroupPageStore = (): InitiateGroupPageStore => {
  return useContext(InitiateGroupPageStoreContext);
};

export { InitiateGroupPageStoreContext };
